// Centralized application logger that mirrors console output while
// capturing log entries for in-app inspection.
const MAX_LOG_ENTRIES = 500;

const logs = [];
const subscribers = new Set();
let counter = 0;

const baseConsole =
  // eslint-disable-next-line no-undef
  typeof globalThis !== 'undefined' && globalThis.console
    // eslint-disable-next-line no-undef
    ? globalThis.console
    : console;

const getCircularReplacer = () => {
  const seen = new WeakSet();
  return (_key, value) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return '[Circular]';
      }
      seen.add(value);
    }
    return value;
  };
};

const formatArg = (arg) => {
  if (typeof arg === 'string') {
    return arg;
  }

  if (arg instanceof Error) {
    return arg.stack || `${arg.name}: ${arg.message}`;
  }

  if (typeof arg === 'object') {
    try {
      return JSON.stringify(arg, getCircularReplacer(), 2);
    } catch (_error) {
      return '[Object]';
    }
  }

  return String(arg);
};

const notifySubscribers = () => {
  const snapshot = logs.slice();
  subscribers.forEach((listener) => listener(snapshot));
};

const addEntry = (level, args) => {
  const entry = {
    id: `${Date.now()}-${counter++}`,
    level,
    message: args.map(formatArg).join(' '),
    args: args.slice(),
    timestamp: new Date().toISOString(),
  };

  logs.push(entry);
  if (logs.length > MAX_LOG_ENTRIES) {
    logs.splice(0, logs.length - MAX_LOG_ENTRIES);
  }

  notifySubscribers();
  return entry;
};

const forwardToConsole = (level, args) => {
  const method = typeof baseConsole[level] === 'function' ? level : 'log';
  try {
    baseConsole[method](...args);
  } catch (_error) {
    // Swallow console errors to avoid breaking log flow.
  }
};

const createLoggerMethod = (level) => {
  return (...args) => {
    forwardToConsole(level, args);
    return addEntry(level, args);
  };
};

const logger = {
  log: createLoggerMethod('log'),
  info: createLoggerMethod('info'),
  warn: createLoggerMethod('warn'),
  error: createLoggerMethod('error'),
  debug: createLoggerMethod('debug'),
  subscribe: (listener) => {
    subscribers.add(listener);
    listener(logs.slice());
    return () => {
      subscribers.delete(listener);
    };
  },
  getLogs: () => logs.slice(),
  clear: () => {
    logs.length = 0;
    notifySubscribers();
  },
};

// eslint-disable-next-line no-undef
if (typeof globalThis !== 'undefined') {
  // eslint-disable-next-line no-undef
  globalThis.appLogger = logger;
}

export const subscribeToLogs = logger.subscribe;
export const getLogs = logger.getLogs;
export const clearLogs = logger.clear;

export default logger;
